import { type MeDto, type SubstitutionClientNoticeDto } from '@bqcrm/shared';
import { Anchor, Checkbox, Group, ScrollArea, Select, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { can } from '../../auth';
import { useOfficeUsers } from '../../data';

/** Osoba zastępująca w formularzu nieobecności; `caseIds` — sprawy do powiadomienia klientów (null — jeszcze nie wybrano). */
export interface DeputyValue {
  deputyId: string | null;
  notifyClients: boolean;
  caseIds: string[] | null;
}

export const NO_DEPUTY: DeputyValue = { deputyId: null, notifyClients: false, caseIds: null };

/** Pola zastępcy do treści żądania (nowa nieobecność / zmiana). */
export const deputyBody = (v: DeputyValue) => ({
  deputyId: v.deputyId,
  notifyClients: !!v.deputyId && v.notifyClients,
  ...(v.deputyId && v.notifyClients && v.caseIds ? { notifyCaseIds: v.caseIds } : {}),
});

/** Zapis zablokowany: zaznaczono powiadomienie klientów, ale bez żadnej sprawy. */
export const deputyIncomplete = (v: DeputyValue) => !!v.deputyId && v.notifyClients && v.caseIds?.length === 0;

/**
 * „Osoba zastępująca” przy nieobecności (docs/16 — zastępstwo): zastępca widzi zadania i powiadomienia nieobecnego,
 * a klienci w strefie i e-mailach — go jako osobę kontaktową. Za siebie wskazuje sam pracownik, za innych — osoba
 * z uprawnieniem „Zastępstwa innych osób”. Opcja „Powiadom klientów” z listą otwartych spraw — gdy wybrano zastępcę.
 */
export function AbsenceDeputyFields({
  me,
  personId,
  value,
  onChange,
  hourly,
  pending,
  caseNumber,
}: {
  me: MeDto;
  personId: string;
  value: DeputyValue;
  onChange: (v: DeputyValue) => void;
  /** nieobecność godzinowa — zastępstwo tylko przy całodniowej */
  hourly: boolean;
  /** wniosek czeka na zatwierdzenie — zastępstwo i e-mail po zatwierdzeniu */
  pending: boolean;
  /** otwarte ze sprawy — domyślnie powiadomienie tylko tej sprawy */
  caseNumber?: string;
}) {
  const users = useOfficeUsers();
  const allowed = can(me, 'zastepstwa.zarzadzanie') || personId === me.id;
  const notice = useQuery({
    queryKey: ['substitution-client-notice', personId],
    queryFn: () => api<SubstitutionClientNoticeDto>(`/substitutions/client-notice?userId=${personId}`),
    enabled: allowed && !!value.deputyId && !hourly,
  });
  if (!allowed)
    return (
      <Text size="xs" c="dimmed">
        Osobę zastępującą za inną osobę wskaże osoba z uprawnieniem „Zastępstwa innych osób”.
      </Text>
    );
  if (hourly)
    return (
      <Text size="xs" c="dimmed">
        Osobę zastępującą wskażesz przy nieobecności całodniowej.
      </Text>
    );
  const people = (users.data ?? []).filter((u) => u.id !== personId).map((u) => ({ value: u.id, label: u.fullName }));
  const mailable = (notice.data?.items ?? []).filter((i) => i.email);
  const defaults = () => {
    const fromCase = mailable.find((i) => i.number === caseNumber);
    return fromCase ? [fromCase.id] : mailable.map((i) => i.id);
  };
  // null — domyślny wybór (wszystkie z e-mailem albo sprawa, z której otwarto okno)
  const selected = value.caseIds ?? (value.notifyClients ? defaults() : []);
  const pick = (ids: string[]) => onChange({ ...value, caseIds: ids });
  return (
    <Stack gap="xs">
      <Select
        label="Osoba zastępująca"
        description={`Widzi zadania i powiadomienia nieobecnego, a klienci (strefa klienta, e-maile) — ją jako osobę kontaktową. ${pending ? 'Zastępstwo zacznie obowiązywać po zatwierdzeniu wniosku, ' : ''}na okres nieobecności.`}
        placeholder="Bez zastępstwa"
        data={people}
        value={value.deputyId}
        onChange={(v) => onChange({ ...value, deputyId: v, ...(v ? {} : { notifyClients: false, caseIds: null }) })}
        searchable
        clearable
      />
      {value.deputyId && (
        <Checkbox
          label={`Powiadom klientów otwartych spraw o osobie kontaktowej${notice.data ? ` (${notice.data.withEmail} z ${notice.data.cases} — klienci z adresem e-mail)` : ''}`}
          description={`E-mail z okresem nieobecności i danymi zastępcy (telefon, e-mail); wpis w historii każdej sprawy.${pending ? ' Wyjdzie po zatwierdzeniu wniosku.' : ''}`}
          checked={value.notifyClients}
          disabled={!notice.data?.withEmail}
          onChange={(e) => onChange({ ...value, notifyClients: e.currentTarget.checked, caseIds: e.currentTarget.checked ? (value.caseIds ?? defaults()) : value.caseIds })}
        />
      )}
      {value.deputyId && value.notifyClients && !!notice.data?.items.length && (
        <Stack gap={4} pl="xl">
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              Wybrane: {selected.length} z {mailable.length}
            </Text>
            <Anchor component="button" type="button" size="xs" onClick={() => pick(mailable.map((i) => i.id))}>
              Zaznacz wszystkie
            </Anchor>
            <Anchor component="button" type="button" size="xs" onClick={() => pick([])}>
              Odznacz
            </Anchor>
          </Group>
          <ScrollArea.Autosize mah={200}>
            <Checkbox.Group value={selected} onChange={pick}>
              <Stack gap={4}>
                {notice.data.items.map((i) => (
                  <Checkbox key={i.id} size="xs" value={i.id} disabled={!i.email} label={`${i.number} — ${i.clientName}${i.email ? '' : ' (brak e-maila)'}`} />
                ))}
              </Stack>
            </Checkbox.Group>
          </ScrollArea.Autosize>
        </Stack>
      )}
    </Stack>
  );
}
